/**
 * `RelayWorker` — a {@link WorkerLike} that hosts the sim Worker inside a
 * nested `sandbox="allow-scripts"` iframe instead of directly on the app
 * thread.
 *
 * Why this exists: the sim worker evaluates UNTRUSTED user/shared sim code
 * (`new Function(...)` in worker.ts). A normal `new Worker()` runs at the app
 * origin and can `indexedDB.open(...)` / read `localStorage`, i.e. the user's
 * Jazz data. Hosting the worker inside an opaque-origin (sandboxed) iframe
 * makes `self.origin === "null"` for the worker, where those storage APIs
 * throw `SecurityError`. The app then talks to the worker only through this
 * relay, which forwards exactly the sim protocol — it is not a generic
 * postMessage proxy.
 *
 * `RelayWorker` satisfies {@link WorkerLike} (the subset of the `Worker`
 * interface {@link SimWorkerBridge} uses), so it drops into the bridge's
 * `createWorker` factory option with no other changes.
 *
 * Transport hardening (vs. the spike's plain window.postMessage):
 * - The handshake runs on `window.postMessage`. The frame can only reach this
 *   relay's listener if `event.source === iframe.contentWindow`; everything
 *   else is dropped. The frame's opaque origin cannot be named, so the
 *   handshake reply uses `targetOrigin: "*"` (the frame validates `source` on
 *   its side).
 * - On handshake the relay transfers ONE `MessagePort` to the frame. All
 *   subsequent sim traffic flows point-to-point over that port — no global
 *   `message` listener, so no cross-frame confused-deputy surface for the
 *   per-command path.
 * - Both directions are allowlisted: only known `SimCommand` discriminants go
 *   app→worker and only known `SimMessage` discriminants come worker→app.
 * - Commands posted before the port is ready are queued and flushed on ready.
 * - Iframe load failure / handshake timeout surface as an `onerror` call so
 *   the bridge's error path fires instead of hanging forever.
 */

import type { WorkerLike, SimCommand, SimMessage } from "@diagram/sim-worker";

/** Path of the sandbox runtime document (see sandbox-runtime.html). */
const SANDBOX_RUNTIME_PATH = "/sandbox-runtime.html";

/**
 * How long to wait for the iframe to load and complete the port handshake
 * before declaring the relay dead. Generous enough for a cold document +
 * worker-bundle fetch on a slow link, short enough that a wedged frame does
 * not hang the simulation UI indefinitely.
 */
const HANDSHAKE_TIMEOUT_MS = 10_000;

/** Handshake marker the relay posts to the frame, carrying the MessagePort. */
const HANDSHAKE_TYPE = "vibediagram-sandbox-handshake";

/** The `SimCommand` discriminants the relay forwards to the worker. */
const ALLOWED_COMMANDS: ReadonlySet<SimCommand["type"]> = new Set([
  "init",
  "preview",
  "start",
  "pause",
  "step",
  "setSpeed",
  "setInput",
  "requestSnapshot",
]);

/** The `SimMessage` discriminants the relay forwards back to the bridge. */
const ALLOWED_MESSAGES: ReadonlySet<SimMessage["type"]> = new Set([
  "initialized",
  "previewResult",
  "previewError",
  "snapshot",
  "done",
  "inputsRegistered",
  "error",
]);

function isSimCommand(value: unknown): value is SimCommand {
  return (
    typeof value === "object" &&
    value !== null &&
    "type" in value &&
    typeof (value as { type: unknown }).type === "string" &&
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- narrowed by the typeof/in checks above
    ALLOWED_COMMANDS.has((value as SimCommand).type)
  );
}

function isSimMessage(value: unknown): value is SimMessage {
  return (
    typeof value === "object" &&
    value !== null &&
    "type" in value &&
    typeof (value as { type: unknown }).type === "string" &&
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- narrowed by the typeof/in checks above
    ALLOWED_MESSAGES.has((value as SimMessage).type)
  );
}

export class RelayWorker implements WorkerLike {
  onmessage: ((event: MessageEvent) => void) | null = null;
  onerror: ((event: ErrorEvent) => void) | null = null;

  private readonly iframe: HTMLIFrameElement;
  private readonly channel: MessageChannel;
  /** Commands posted before the port handshake completed; flushed on ready. */
  private readonly queue: SimCommand[] = [];
  private ready = false;
  private terminated = false;
  private handshakeTimer: ReturnType<typeof setTimeout> | undefined;
  private readonly onWindowMessage: (event: MessageEvent<unknown>) => void;
  private readonly onIframeError: () => void;

  constructor(runtimeUrl: string = SANDBOX_RUNTIME_PATH) {
    this.channel = new MessageChannel();
    // Worker→app traffic: the frame holds port2; we keep port1.
    this.channel.port1.onmessage = (event: MessageEvent<unknown>) => {
      this.handlePortMessage(event.data);
    };

    this.iframe = document.createElement("iframe");
    // allow-scripts only → opaque origin → no app-origin storage access.
    this.iframe.setAttribute("sandbox", "allow-scripts");
    this.iframe.style.display = "none";
    this.iframe.src = runtimeUrl;

    // The frame announces readiness on window.postMessage (it cannot receive a
    // transferred port without us first sending it one, so the handshake must
    // start over the window channel). We reply by transferring port2.
    this.onWindowMessage = (event: MessageEvent<unknown>) => {
      if (event.source !== this.iframe.contentWindow) return;
      const data = event.data;
      if (typeof data !== "object" || data === null) return;
      if (!("type" in data)) return;
      if ((data as { type: unknown }).type !== HANDSHAKE_TYPE) return;
      this.completeHandshake();
    };
    window.addEventListener("message", this.onWindowMessage);

    // A failed document load never fires the readiness handshake; the iframe
    // `error` event (and the timeout below) are the only signals we get.
    this.onIframeError = () => {
      this.fail("Sandbox runtime iframe failed to load");
    };
    this.iframe.addEventListener("error", this.onIframeError);

    this.handshakeTimer = setTimeout(() => {
      this.fail("Sandbox runtime handshake timed out");
    }, HANDSHAKE_TIMEOUT_MS);

    document.body.appendChild(this.iframe);
  }

  postMessage(message: unknown): void {
    if (this.terminated) return;
    if (!isSimCommand(message)) return; // drop non-protocol messages app→frame
    if (!this.ready) {
      this.queue.push(message);
      return;
    }
    this.channel.port1.postMessage(message);
  }

  terminate(): void {
    if (this.terminated) return;
    this.terminated = true;
    if (this.handshakeTimer !== undefined) clearTimeout(this.handshakeTimer);
    window.removeEventListener("message", this.onWindowMessage);
    this.iframe.removeEventListener("error", this.onIframeError);
    this.channel.port1.onmessage = null;
    this.channel.port1.close();
    this.iframe.remove();
  }

  /** Transfer port2 to the frame and flush any queued commands. */
  private completeHandshake(): void {
    if (this.terminated || this.ready) return;
    if (this.handshakeTimer !== undefined) {
      clearTimeout(this.handshakeTimer);
      this.handshakeTimer = undefined;
    }
    // targetOrigin "*" — the sandboxed frame's origin is opaque and cannot be
    // named. The frame validated us via `event.source` before announcing.
    this.iframe.contentWindow?.postMessage({ type: HANDSHAKE_TYPE }, "*", [
      this.channel.port2,
    ]);
    this.ready = true;
    for (const cmd of this.queue.splice(0)) {
      this.channel.port1.postMessage(cmd);
    }
  }

  /** Forward an allowlisted worker→app message; drop anything else. */
  private handlePortMessage(data: unknown): void {
    if (this.terminated) return;
    if (!isSimMessage(data)) return;
    // The bridge only reads `.data`; deliver a real MessageEvent so the
    // WorkerLike contract (a `MessageEvent` handler) holds without a cast.
    this.onmessage?.(new MessageEvent("message", { data }));
  }

  /**
   * Surface a fatal relay condition as an `onerror`. The bridge maps this to
   * an error status and rejects any pending init/snapshot, so the UI shows a
   * real error rather than spinning forever.
   */
  private fail(message: string): void {
    if (this.terminated || this.ready) return;
    if (this.handshakeTimer !== undefined) {
      clearTimeout(this.handshakeTimer);
      this.handshakeTimer = undefined;
    }
    this.onerror?.(new ErrorEvent("error", { message }));
  }
}
